import { makeStyles } from 'tss-react/mui';
import {
    TimesheetTemplate,
    Timesheet,
    TimesheetDay,
    TimesheetDayInterval,
} from 'api/report/types';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Skeleton from '@mui/material/Skeleton';
import NumberField from 'components/interface/textField/formats/NumberField';
import TimePicker from 'components/TimePicker';
import { WEEKDAY } from '../utils';
import { red, pink } from '@mui/material/colors';
import { lighten } from '@mui/material';
import Tooltip from 'components/interface/tooltip/Tooltip';
import Symbol from 'components/Symbol';

const useStyles = makeStyles()(theme => ({
    table: {
        marginBottom: theme.spacing(2),
        background: '#fff',
    },
    headerCell: {
        background: theme.palette.primary.main,
        color: '#fff',
        fontSize: '14px',
    },
    subheaderCell: {
        background: theme.palette.grey[200],
        color: theme.palette.grey[800],
        fontSize: '14px',
        fontWeight: '400',
    },
    cell: {
        height: '48px',
        wordBreak: 'break-word',
        hyphens: 'auto',
        textAlign: 'center',
        paddingLeft: theme.spacing(2),
        paddingRight: theme.spacing(2),
        border: '1px solid ' + theme.palette.grey[800],
    },
    cellSums: {
        background: theme.palette.primary['200'],
    },
    publicHoliday: {
        color: pink[900],
        background: pink[50],
    },
    isSickHours: {
        background: lighten(red[50], 0.4),
        borderTop: '2px dotted ' + theme.palette.grey[500],
    },
    sickHoursIcon: {
        color: red[900],
    },
    noPadding: {
        padding: 0,
    },
}));

interface Props {
    timesheet: Timesheet;
    totalHours: string;
    template: TimesheetTemplate;
    canSubmit: boolean;
    disabled: boolean;
    loading: boolean;
    enableSickHours: boolean;
    onUpdateTimesheetDay: (
        updateFunc: (day: TimesheetDay) => TimesheetDay,
        index: number
    ) => void;
}

export default function StartStopTimesheet({
    timesheet,
    totalHours,
    template,
    canSubmit,
    disabled,
    loading,
    enableSickHours,
    onUpdateTimesheetDay,
}: Props): JSX.Element {
    const { classes, cx } = useStyles();

    const renderHeaderCell = (text: string, key: number) => {
        return (
            <TableCell
                align='center'
                key={key}
                colSpan={1}
                className={cx(classes.headerCell, classes.cell)}>
                {text}
            </TableCell>
        );
    };

    const renderFooter = () => {
        const { totalTitle, footnote } = template;
        return (
            <TableRow>
                <TableCell className={classes.cell}>
                    {loading ? (
                        <Skeleton
                            animation='pulse'
                            variant='text'
                            width='96px'
                        />
                    ) : (
                        totalTitle
                    )}
                </TableCell>
                <TableCell
                    colSpan={1}
                    className={cx(classes.cell, classes.cellSums)}>
                    {loading ? (
                        <Skeleton
                            animation='pulse'
                            variant='text'
                            width='96px'
                        />
                    ) : (
                        totalHours || ''
                    )}
                </TableCell>
                <TableCell
                    colSpan={2}
                    className={classes.cell}
                    {...(loading
                        ? {}
                        : {
                              dangerouslySetInnerHTML: {
                                  __html: footnote?.text || '',
                              },
                          })}>
                    {loading ? (
                        <Skeleton
                            animation='pulse'
                            variant='text'
                            width='300px'
                        />
                    ) : null}
                </TableCell>
            </TableRow>
        );
    };

    const renderPage = (): JSX.Element => {
        return (
            <Table
                className={classes.table}
                padding='none'
                style={{ tableLayout: 'fixed' }}>
                <TableHead>
                    <TableRow>
                        {renderHeaderCell('', 0)}
                        {renderHeaderCell('Start', 1)}
                        {renderHeaderCell('Slut', 2)}
                        {renderHeaderCell('Rast', 3)}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {/* The report endpoint removes start/stop overflow days. */}
                    {(timesheet.days ?? []).map((day, index) => (
                        <Row
                            key={index}
                            {...day}
                            index={index}
                            canSubmit={canSubmit}
                            disabled={disabled}
                            loading={loading}
                            enableSickHours={enableSickHours}
                            onUpdateTimesheetDay={onUpdateTimesheetDay}
                        />
                    ))}
                    {renderFooter()}
                </TableBody>
            </Table>
        );
    };

    return renderPage();
}

interface RowProps extends TimesheetDay {
    index: number;
    canSubmit: boolean;
    disabled: boolean;
    loading: boolean;
    enableSickHours: boolean;
    onUpdateTimesheetDay: (
        updateFunc: (day: TimesheetDay) => TimesheetDay,
        index: number
    ) => void;
}

const Row = ({
    month,
    day,
    weekday,
    holiday,
    intervals,
    index,
    canSubmit,
    disabled,
    loading,
    enableSickHours,
    onUpdateTimesheetDay,
}: RowProps) => {
    const { classes, cx } = useStyles();

    const handleUpdateTimesheetDay = (
        value: Partial<TimesheetDayInterval>,
        columnIndex: number,
        isSickHours: boolean
    ) => {
        onUpdateTimesheetDay(prev => {
            const d = { ...prev };
            d.intervals = [...d.intervals];
            d.intervals[columnIndex] = {
                ...d.intervals[columnIndex],
                ...value,
                isSickHours,
            };

            return d;
        }, index);
    };

    const buildIntervalCells = (
        value: TimesheetDayInterval,
        intervalIndex: number
    ) => {
        return (
            <>
                <TableCell
                    key={intervalIndex + '-start'}
                    colSpan={1}
                    className={cx(classes.cell, classes.noPadding, {
                        [classes.isSickHours]: value.isSickHours,
                    })}>
                    <TimePicker
                        value={value.start || ''}
                        onChange={v =>
                            handleUpdateTimesheetDay(
                                { start: v },
                                intervalIndex,
                                value.isSickHours
                            )
                        }
                        error={!value?.start && !!value?.stop}
                        disabled={!canSubmit || disabled}
                        variant='sheet'
                        height={48}
                        startAdornment={
                            value.isSickHours ? (
                                <Symbol
                                    size='md'
                                    symbol='sick'
                                    color='red-dark'
                                    tooltip='Sjuktimmar'
                                    sx={{
                                        paddingRight: 2,
                                    }}
                                />
                            ) : undefined
                        }
                        variantSx={{
                            backgroundColor: value.isSickHours
                                ? 'transparent'
                                : undefined,
                        }}
                        onKeyDown={event => {
                            // disables navigation
                            if (
                                enableSickHours &&
                                event.key.startsWith('Arrow')
                            ) {
                                event.stopPropagation();
                                event.preventDefault();
                            }
                        }}
                    />
                </TableCell>
                <TableCell
                    key={intervalIndex + '-stop'}
                    colSpan={1}
                    className={cx(classes.cell, classes.noPadding, {
                        [classes.isSickHours]: value.isSickHours,
                    })}>
                    <TimePicker
                        value={value?.stop || ''}
                        onChange={v =>
                            handleUpdateTimesheetDay(
                                { stop: v },
                                intervalIndex,
                                value.isSickHours
                            )
                        }
                        error={!value?.stop && !!value?.start}
                        disabled={!canSubmit || disabled}
                        variant='sheet'
                        height={48}
                        variantSx={{
                            backgroundColor: value.isSickHours
                                ? 'transparent'
                                : undefined,
                        }}
                        onKeyDown={event => {
                            // disables navigation
                            if (
                                enableSickHours &&
                                event.key.startsWith('Arrow')
                            ) {
                                event.stopPropagation();
                                event.preventDefault();
                            }
                        }}
                    />
                </TableCell>
                <TableCell
                    key={intervalIndex + '-break'}
                    colSpan={1}
                    className={cx(classes.cell, classes.noPadding, {
                        [classes.isSickHours]: value.isSickHours,
                    })}>
                    {value.isSickHours ? null : (
                        <NumberField
                            variant='sheet'
                            value={value?.breakInMinutes || 0}
                            onChange={v =>
                                handleUpdateTimesheetDay(
                                    { breakInMinutes: v },
                                    intervalIndex,
                                    value.isSickHours
                                )
                            }
                            endAdornment={
                                <Typography variant='body2'>minuter</Typography>
                            }
                            height={48}
                            disabled={!canSubmit || disabled}
                            allowZero
                            maxValue={300}
                            onKeyDown={event => {
                                // disables navigation
                                if (
                                    enableSickHours &&
                                    event.key.startsWith('Arrow')
                                ) {
                                    event.stopPropagation();
                                    event.preventDefault();
                                }
                            }}
                        />
                    )}
                </TableCell>
            </>
        );
    };

    return (
        <>
            <TableRow>
                <TableCell
                    className={cx(classes.cell, {
                        [classes.publicHoliday]: holiday?.IsPublicHoliday,
                    })}
                    rowSpan={enableSickHours ? 2 : 1}>
                    {loading ? (
                        <Skeleton
                            animation='pulse'
                            variant='text'
                            width='96px'
                        />
                    ) : (
                        <Tooltip
                            enterTouchDelay={500}
                            title={holiday?.Name}
                            placement='top'
                            disableInteractive>
                            <span>
                                <Typography variant='body2' color='inherit'>
                                    {`${WEEKDAY[weekday]} ${day}/${month}`}
                                </Typography>
                                {holiday?.IsMajorHoliday ? (
                                    <Typography
                                        variant='caption'
                                        color='inherit'>
                                        storhelg
                                    </Typography>
                                ) : null}
                            </span>
                        </Tooltip>
                    )}
                </TableCell>
                {buildIntervalCells(
                    intervals && intervals[0]
                        ? intervals[0]
                        : {
                              start: '',
                              stop: '',
                              breakInMinutes: 0,
                              isSickHours: false,
                          },
                    0
                )}
            </TableRow>
            {enableSickHours && (
                <TableRow>
                    {buildIntervalCells(
                        intervals && intervals[1]
                            ? intervals[1]
                            : {
                                  start: '',
                                  stop: '',
                                  breakInMinutes: 0,
                                  isSickHours: true,
                              },
                        1
                    )}
                </TableRow>
            )}
        </>
    );
};
